import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Star, User as UserIcon, Phone, Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Customers() {
  const { company } = useAuth();
  const currency = company?.currency || "FCFA";
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/customers").then((r) => setCustomers(r.data));
  useEffect(() => { load(); }, []);

  const filtered = customers.filter((c) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [c.firstname, c.name, c.phone].filter(Boolean).some((v) => v.toLowerCase().includes(s));
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Clients</h1>
          <p className="text-sm text-slate-500">Fichier des clients de l'agence</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 min-w-[220px]" data-testid="customers-search-input" />
          </div>
          <NewCustomerDialog onCreated={load} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <Card key={c.id} data-testid={`customer-card-${c.id}`} className="hover:shadow-md hover:-translate-y-0.5 transition-all">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-orange-100 dark:bg-orange-500/20 text-orange-500 grid place-items-center">
                  <UserIcon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{c.firstname} {c.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</div>
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className={`w-3 h-3 ${i <= (c.rating || 0) ? "fill-orange-500 stroke-orange-500" : "stroke-slate-300"}`} />
                      ))}
                    </div>
                    <span className="text-slate-500">{c.trips} voyage(s)</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase text-slate-500">Dépensé</div>
                  <div className="font-display font-semibold">{formatCurrency(c.spent, currency)}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center py-10 text-slate-500">Aucun client</div>}
      </div>
    </div>
  );
}

function NewCustomerDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ firstname: "", name: "", phone: "" });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-pill bg-orange-500 hover:bg-orange-600" data-testid="add-customer-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau client</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nouveau client</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Prénom</Label><Input value={form.firstname} onChange={(e) => setForm({ ...form, firstname: e.target.value })} data-testid="new-cust-first" /></div>
          <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-cust-last" /></div>
          <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="new-cust-phone" /></div>
          <Button className="btn-pill bg-orange-500 hover:bg-orange-600 w-full" data-testid="save-customer-btn" onClick={async () => {
            try { await api.post("/customers", form); toast.success("Client ajouté"); setOpen(false); setForm({ firstname: "", name: "", phone: "" }); onCreated(); }
            catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
          }}>Enregistrer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
